import type { DimSumEnhancedNode, RootNodesForDimensionsResponse } from '@amzn/gft-dimsum-service';
import { DEFAULT_PAGE_DRIVERS, KPI_DIMENSION_RENDER_MAP } from '@constants/page';
import {
  DatasetSource,
  GalaxyKPIDrivers,
  GalaxyKPIDriversInput,
  KPIDriverEntryInput,
  KPIDrivers,
} from '@services/api/deckManagement/graphql/schema/API';
import { isNil, omitBy } from 'lodash';
import { PartialKPIDrivers } from 'types/Page.types';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import {
  AnyKPIDrivers,
  BaseKpiDimensionFieldTypes,
  KpiDimension,
  KpiDimensionConfig,
  KpiDriverValues,
} from 'types/PageSidePanel.types';

/**
 * Splits a DimSum node id into its hierarchy prefix and bare node value.
 *
 * A resolved DimSum node's id is encoded `<HIERARCHY>-<node>` (e.g.
 * `COGNOS_REVGP_SEGMENT_HIERARCHY-L17500`). The hierarchy name itself contains no bare `-` before the
 * suffix join, so the FIRST `-` is the split point; any further `-` belong to the node value and are
 * rejoined. Returns `{ value, hierarchy: null }` when the id has no `-` (nothing to split), and
 * `{ value: '', hierarchy: null }` for a nil/empty id.
 *
 * This is the single source of truth for the `<HIERARCHY>-<node>` convention used across the KPI
 * driver and calculated-KPI code (`convertDimSumNodeToKpiDriverValues`, calc_members serialization).
 *
 * @param nodeID a DimSum node id, possibly `<HIERARCHY>-<node>`
 */
export const splitNodeId = (nodeID: string | null | undefined): { value: string; hierarchy: string | null } => {
  if (isNil(nodeID) || nodeID === '') {
    return { value: '', hierarchy: null };
  }
  const dashIndex = nodeID.indexOf('-');
  if (dashIndex <= 0) {
    return { value: nodeID, hierarchy: null };
  }
  return { value: nodeID.substring(dashIndex + 1), hierarchy: nodeID.substring(0, dashIndex) };
};

/**
 * Resolves the {@link DatasetSource} a driver set belongs to.
 *
 * Prefers the explicit `datasetSource` field the `KPIDrivers` interface now carries; falls back to
 * the concrete wire `__typename` (`HctrKPIDrivers`), then to GALAXY when neither is present (e.g. a
 * locally-constructed or partial object), matching the single-dataset default the FE has assumed.
 *
 * @param drivers a driver set that may carry a `datasetSource` / `__typename` discriminator
 * @returns HCTR when the set is HCTR-shaped, otherwise GALAXY
 */
export const datasetSourceFromDrivers = (drivers?: AnyKPIDrivers | null): DatasetSource => {
  if (!isNil(drivers?.datasetSource)) return drivers.datasetSource;
  return drivers?.__typename === 'HctrKPIDrivers' ? DatasetSource.HCTR : DatasetSource.GALAXY;
};

/**
 * A variant's resolved drivers: its overrides over a baseline (the page's drivers or denominator drivers),
 * the order the backend derives them in. A served `variant_overrides` carries every field, null where the
 * variant does not override, so only non-null overrides replace the baseline's values.
 * @param baseline - The inherited drivers
 * @param overrides - The variant's overrides
 */
export const applyVariantOverrides = (
  baseline: AnyKPIDrivers | null | undefined,
  overrides: AnyKPIDrivers | null | undefined,
): AnyKPIDrivers => ({ ...(baseline ?? {}), ...omitBy(overrides ?? {}, isNil) }) as AnyKPIDrivers;

/** A GalaxyKPIDrivers object with all fields set to null. Used when initializing empty deckline drivers. */
export const EMPTY_KPI_DRIVERS: GalaxyKPIDrivers = {
  __typename: 'GalaxyKPIDrivers',
  account_hierarchy: null,
  account: null,
  location_hierarchy: null,
  location: null,
  project_hierarchy: null,
  project: null,
  company_hierarchy: null,
  company: null,
  product_hierarchy: null,
  product: null,
  revgp_hierarchy: null,
  revgp: null,
  datasource_hierarchy: null,
  datasource: null,
  channel_hierarchy: null,
  channel: null,
  costcenter_hierarchy: null,
  costcenter: null,
  costtype: null,
  costpool: null,
};

/** A KPIDriversInput object with all fields set to null. Used when initializing empty variant overrides. */
export const EMPTY_KPI_DRIVERS_INPUT: GalaxyKPIDriversInput = {
  account_hierarchy: null,
  account: null,
  location_hierarchy: null,
  location: null,
  project_hierarchy: null,
  project: null,
  company_hierarchy: null,
  company: null,
  product_hierarchy: null,
  product: null,
  revgp_hierarchy: null,
  revgp: null,
  datasource_hierarchy: null,
  datasource: null,
  channel_hierarchy: null,
  channel: null,
  costcenter_hierarchy: null,
  costcenter: null,
  costtype: null,
  costpool: null,
};

/**
 * Sanitizes KPI Drivers by trimming string values
 * @param kpiDrivers - The KPI Drivers to sanitize
 * @returns The sanitized KPI Drivers
 */
export const sanitizeKPIDrivers = (kpiDrivers: KPIDrivers | null | undefined): KPIDrivers | null | undefined => {
  if (!kpiDrivers) {
    return kpiDrivers;
  }

  // Trim all string fields (dataset-agnostic — operates on any KPIDrivers shape)
  return Object.fromEntries(
    Object.entries(kpiDrivers).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]),
  ) as KPIDrivers;
};

/**
 * Produces the "effective" drivers for a deckline by merging page-level drivers with deckline-level overrides.
 * Null values in deckline drivers are treated as "inherit from page." Non-null deckline values take precedence.
 *
 * @param decklineDrivers - The deckline's sparse driver overrides (null values = inherit)
 * @param pageDrivers - The page-level default drivers
 * @returns The merged effective drivers, or null if neither source is available
 */
export const getEffectiveDrivers = (
  decklineDrivers: AnyKPIDrivers | null | undefined,
  pageDrivers: AnyKPIDrivers | null | undefined,
): AnyKPIDrivers | null => {
  if (!pageDrivers) return decklineDrivers ?? null;
  if (!decklineDrivers) return pageDrivers;

  const decklineBag = decklineDrivers as Record<string, unknown>;
  return Object.entries(pageDrivers).reduce((acc, [key, pageValue]) => {
    // `__typename` / `datasetSource` are dataset discriminators, not dimensions: carry them through
    // from the page set rather than merging them as driver values.
    if (key === '__typename' || key === 'datasetSource') {
      (acc as Record<string, unknown>)[key] = pageValue;
    } else {
      (acc as Record<string, unknown>)[key] = decklineBag[key] ?? pageValue;
    }
    return acc;
  }, {} as AnyKPIDrivers);
};

/**
 * Checks if a KPI Driver is empty, which can be true when the kpiDrivers object is null, or when every value within it is nil.
 * @param kpiDrivers
 */
export const kpiDriversEmpty = (kpiDrivers?: GalaxyKPIDrivers | null) => {
  if (isNil(kpiDrivers)) {
    return true;
  }

  // Check if every dimension value is nil (ignoring the `__typename` / `datasetSource` discriminators)
  return Object.keys(kpiDrivers)
    .filter((key) => key !== '__typename' && key !== 'datasetSource')
    .every((key) => isNil(kpiDrivers[key as keyof GalaxyKPIDrivers]));
};

/**
 * Generates Driver Display Options.
 *
 * NOTE:
 *   * When `parentKpiDrivers` is empty, then `kpiDrivers` is referring to the Page Drivers.
 *   * When `parentKpiDrivers` is not empty, then `kpiDrivers` is referring to the Deckline Drivers, and `parentKpiDrivers` is referring to the Page Drivers.
 * @param kpiDrivers - The KPI drivers to generate display options for
 * @param parentKpiDrivers - Optional parent KPI drivers for comparison
 * @param alwaysActive - List of KpiDimensions to force as active regardless of comparison logic
 * @param datasetSourceOverride - Which dataset's dimension render list to iterate; defaults to the dataset
 *   resolved from `kpiDrivers.__typename` (GALAXY when absent). Pass explicitly to override.
 */
export const generateDriverDisplayOptions = (
  kpiDrivers?: AnyKPIDrivers | null,
  parentKpiDrivers?: AnyKPIDrivers | null,
  alwaysActive?: KpiDimension[],
  datasetSourceOverride?: DatasetSource | null,
) => {
  const datasetSource: DatasetSource = datasetSourceOverride ?? datasetSourceFromDrivers(kpiDrivers);
  const result: DriverDisplayOptionMap = {};
  if (!isNil(kpiDrivers)) {
    KPI_DIMENSION_RENDER_MAP[datasetSource].forEach((kpiConfig) => {
      const { decklineDrivers, key, baseKpiField } = kpiConfig;
      const kpiDimension = key as KpiDimension;

      if (kpiDimension) {
        if (baseKpiField.type === BaseKpiDimensionFieldTypes.ReadOnly && !isNil(baseKpiField.staticValue)) {
          result[kpiDimension] = {
            kpiDimension,
            value: baseKpiField.staticValue,
            // For static readonly values, `active` is TRUE for pageDrivers, and FALSE for Deckline Drivers.
            // (`parentKpiDrivers` will be undefined/null when we're processing Page Drivers through this function)
            active: isNil(parentKpiDrivers),
          };
        } else if (decklineDrivers) {
          const { valueAccessor, hierarchyAccessor } = decklineDrivers;
          const driverValue = kpiDrivers[valueAccessor];
          const hierarchyValue = hierarchyAccessor ? kpiDrivers[hierarchyAccessor] : null;
          const parentDriverValue = parentKpiDrivers?.[valueAccessor];
          const parentHierarchyValue = hierarchyAccessor ? parentKpiDrivers?.[hierarchyAccessor] : null;
          let activeState: boolean;
          if (alwaysActive?.includes(kpiDimension)) {
            activeState = true;
          } else if (isNil(driverValue) && !isNil(parentDriverValue)) {
            // Null deckline value with a non-null page value = inherited from page (cascading) = not active
            activeState = false;
          } else if (isNil(driverValue) && isNil(parentDriverValue)) {
            // Both null = no value to inherit, user needs to provide one = active
            activeState = true;
          } else {
            activeState = driverValue !== parentDriverValue || hierarchyValue !== parentHierarchyValue;
          }

          result[kpiDimension] = {
            kpiDimension,
            // When value is null (cascading), display the inherited parent value
            hierarchy: hierarchyValue ?? parentHierarchyValue ?? '',
            value: driverValue ?? parentDriverValue ?? '',
            active: activeState,
          };
        }
      }
    });
  }
  return result;
};

/**
 * Generates a base KPI ID for the given segment data
 * @param segmentDisplayData
 * @param rootNodes - Needed for determining if a segment's node is a Root Node or not.
 * @param datasetSource - Which dataset's dimension render list to iterate; defaults to GALAXY
 */
export const generateBaseKpiId = (
  segmentDisplayData: DriverDisplayOptionMap,
  rootNodes: RootNodesForDimensionsResponse,
  datasetSource: DatasetSource = DatasetSource.GALAXY,
) => {
  // Determines this KPI Dimensions need to be included in the KPI ID. Return `null` when a dimension isn't needed.
  const targets = KPI_DIMENSION_RENDER_MAP[datasetSource]
    .map((config) => {
      const { key, formulaKey, baseKpiField } = config;

      // Dimension Picker Fields
      if (baseKpiField.type === BaseKpiDimensionFieldTypes.DimensionPicker) {
        const segmentHierarchy = segmentDisplayData[key]?.hierarchy;
        const segmentValue = segmentDisplayData[key]?.value;
        const dimSumNodeId = [segmentHierarchy, segmentValue].join('-');
        const isRootNode = rootNodes?.[key]?.some((node) => {
          return node?.nodeID === dimSumNodeId;
        });

        // We need to include the dimension with the node is NOT a Root Node.
        if (!isRootNode) {
          return `${formulaKey}:${segmentValue}`;
        }

        // Don't include this node for generating the KPI ID.
        return null;
      }

      // Select Fields
      if (baseKpiField.type === BaseKpiDimensionFieldTypes.Select) {
        const segmentValue = segmentDisplayData[key]?.value;

        if (segmentValue !== baseKpiField.rootValue) {
          return `${formulaKey}:${segmentValue}`;
        }

        // Don't include this node for generating the KPI ID.
        return null;
      }

      // Unhandled Field Types
      return null;
    })
    .filter((target) => !isNil(target));

  // Construct the KPI ID string
  return `|${targets.join('|')}|`;
};

/**
 * Checks if deckline drivers match page KPI drivers for all non-null page driver values
 * @param decklineDrivers - The deckline's KPI drivers
 * @param pageDrivers - The page's KPI drivers
 * @returns true if all non-null page driver values match deckline values, false otherwise
 */
export const hasDriverMismatch = (
  decklineDrivers?: AnyKPIDrivers | null,
  pageDrivers?: AnyKPIDrivers | null,
): boolean => {
  if (!decklineDrivers || !pageDrivers) return false;

  const decklineBag = decklineDrivers as Record<string, unknown>;
  const defaults = DEFAULT_PAGE_DRIVERS as Record<string, unknown>;
  return Object.entries(pageDrivers).some(([key, pageValue]) => {
    if (key === '__typename' || key === 'datasetSource') return false;
    const decklineValue = decklineBag[key];
    if (isNil(decklineValue)) return false; // null = inherited from page = not a mismatch
    if (isNil(pageValue)) return false;
    if (pageValue === defaults[key]) return false; // page at default = not an exception
    return pageValue !== decklineValue;
  });
};

/**
 * Returns only the GalaxyKPIDrivers keys whose values differ between two driver objects.
 * Companion to {@link hasDriverMismatch} — use this when you need the actual changed values.
 *
 * @param original - The original driver values
 * @param current - The current driver values
 * @returns An object containing only the keys/values that changed
 */
export const diffKPIDrivers = (original: GalaxyKPIDrivers, current: GalaxyKPIDrivers): PartialKPIDrivers =>
  Object.keys(current).reduce((acc, key) => {
    if (
      key !== '__typename' &&
      key !== 'datasetSource' &&
      current[key as keyof GalaxyKPIDrivers] !== original[key as keyof GalaxyKPIDrivers]
    ) {
      acc[key as keyof PartialKPIDrivers] = current[
        key as keyof GalaxyKPIDrivers
      ] as PartialKPIDrivers[keyof PartialKPIDrivers];
    }
    return acc;
  }, {} as PartialKPIDrivers);

/**
 * Narrows a `KPIDrivers` union value to the Galaxy shape the FE works with.
 *
 * Every driver-consuming path here is Galaxy-only; HCTR driver panels are Epic H. Returns null for an
 * HCTR set rather than mis-typing it as Galaxy, so a caller gets "no drivers" instead of a silent
 * object whose named dimensions are all undefined.
 *
 * @param drivers a driver set from the wire, of either dataset shape
 * @returns the Galaxy driver set, or null when absent or HCTR-shaped
 */
export const asGalaxyDrivers = (drivers?: AnyKPIDrivers | null): GalaxyKPIDrivers | null => {
  if (isNil(drivers)) return null;
  // Cast via `unknown`: the concrete wire object IS a Galaxy set, but codegen types the interface's
  // `__typename` as the literal "KPIDrivers", which doesn't structurally overlap GalaxyKPIDrivers.
  return datasetSourceFromDrivers(drivers) === DatasetSource.HCTR ? null : (drivers as unknown as GalaxyKPIDrivers);
};

/**
 * Widens a concrete driver set (Galaxy/HCTR) or the FE's dataset-agnostic bag back to the `KPIDrivers`
 * interface type that model fields (`deckline_drivers`, `page_drivers`, `variant_overrides`) are typed
 * as. A concrete set structurally satisfies the interface; only codegen's fixed
 * `__typename: "KPIDrivers"` blocks a direct assignment, so this casts via `unknown`.
 *
 * @param drivers a concrete or bag driver set the FE holds
 * @returns the same object typed as `KPIDrivers`, or null when absent
 */
export const asKPIDrivers = (drivers?: AnyKPIDrivers | null): KPIDrivers | null =>
  isNil(drivers) ? null : (drivers as unknown as KPIDrivers);

/**
 * Converts a flat KPI driver object into the `[{ key, value }]` list the KPI-variant mutations now
 * take.
 *
 * `CreateKPIVariantInput.variant_overrides` and `UpdateKPIVariantInput.variant_overrides` changed
 * from a flat `GalaxyKPIDriversInput` object to `[KPIDriverEntryInput!]` when the BE added HCTR
 * support — a key/value list describes either dataset's dimensions without the input type having to
 * name them. Callers still hold flat objects, so this is the boundary adapter.
 *
 * `__typename`, null/undefined and empty-string values are dropped: a dimension without an override
 * must be ABSENT so it cascades from `page_drivers`, rather than being sent as an empty override.
 *
 * @param drivers a flat driver object (Galaxy or HCTR shaped), or nil
 * @returns the driver entries to send, empty when there is nothing to override
 */
export const toKPIDriverEntries = (drivers?: Record<string, unknown> | null): KPIDriverEntryInput[] =>
  Object.entries(drivers ?? {})
    .filter(([key, value]) => key !== '__typename' && !isNil(value) && value !== '')
    .map(([key, value]) => ({ key, value: String(value) }));

/**
 * Resolves the default {@link DatasetSource} for a newly-created variant on a page: GALAXY when the page
 * configures a GALAXY `page_drivers` entry, otherwise the first configured dataset. A new variant needs a
 * valid starting dataset the page actually supports; the choice stays editable via the side-panel Source
 * selector.
 *
 * @param pageDriverSets the page's per-dataset driver map
 * @returns GALAXY when configured, else the first configured dataset, else GALAXY as a last resort
 */
export const resolveDefaultVariantDataset = (
  pageDriverSets: Partial<Record<DatasetSource, AnyKPIDrivers>> | null | undefined,
): DatasetSource => {
  const configured = Object.keys(pageDriverSets ?? {}) as DatasetSource[];
  if (configured.includes(DatasetSource.GALAXY)) return DatasetSource.GALAXY;
  return configured[0] ?? DatasetSource.GALAXY;
};

/**
 * Reads a Page's `page_drivers` list into the per-dataset map the FE works with.
 *
 * `Page.page_drivers` is `[KPIDrivers]` on the wire — one entry per configured dataset. Each entry is
 * keyed by the {@link DatasetSource} resolved from the set itself (`datasetSource` / `__typename`), so a
 * page configured for any subset of datasets (Galaxy-only, HCTR-only, both) reads back faithfully.
 *
 * @param pageDrivers the raw `page_drivers` list from a Page
 * @returns a map of dataset → driver set (empty when there are none)
 */
export const readPageDriverSets = (
  pageDrivers?: Array<KPIDrivers | null> | null,
): Partial<Record<DatasetSource, AnyKPIDrivers>> =>
  (pageDrivers ?? [])
    .filter((set): set is KPIDrivers => !isNil(set))
    .reduce<Partial<Record<DatasetSource, AnyKPIDrivers>>>((acc, set) => {
      acc[datasetSourceFromDrivers(set)] = set;
      return acc;
    }, {});

/**
 * Selects a single dataset's driver set from a per-dataset map.
 *
 * @param driverSets the per-dataset map (see {@link readPageDriverSets})
 * @param datasetSource which dataset's set to read
 * @returns that dataset's driver set, or null when it is not configured
 */
export const readPageDrivers = (
  driverSets: Partial<Record<DatasetSource, AnyKPIDrivers>> | undefined | null,
  datasetSource: DatasetSource,
): AnyKPIDrivers | null => driverSets?.[datasetSource] ?? null;

/**
 * Wraps the per-dataset driver map back into the union list `page_drivers` expects on write.
 *
 * Emits one union-wrapped entry per configured dataset, keyed by the dataset name lower-cased
 * (`GALAXY` → `galaxy`, `HCTR` → `hctr`), which is the shape the `KPIDriversInput` union expects.
 * Sending a flat object instead parses to `{}` through the zod schema and wipes the page's drivers.
 *
 * @param driverSets the per-dataset map the FE holds
 * @returns one union-wrapped entry per configured dataset
 */
export const writePageDriverSets = (
  driverSets: Partial<Record<DatasetSource, AnyKPIDrivers>>,
): Array<Record<string, AnyKPIDrivers>> =>
  Object.entries(driverSets)
    .filter(([, drivers]) => !isNil(drivers))
    .map(([dataset, drivers]) => ({ [dataset.toLowerCase()]: { ...drivers } }));

/**
 * Converts KPI driver values (hierarchy + dimension value) into a DimSum node ID string.
 * The node ID format is `{hierarchyValue}-{dimensionValue}`.
 *
 * @param kpiDrivers - The KPI driver values to convert
 * @param kpiConfig - The KPI dimension configuration containing accessor mappings
 * @returns The composed DimSum node ID, or null if required values are missing
 */
export const convertKpiDriverValuesToDimSumNodeId = (
  kpiDrivers: Partial<AnyKPIDrivers> | null,
  kpiConfig: KpiDimensionConfig<KpiDimension>,
): string | null => {
  if (isNil(kpiDrivers)) {
    return null;
  }

  if (kpiConfig.baseKpiField.type !== BaseKpiDimensionFieldTypes.DimensionPicker) {
    return null;
  }

  const { decklineDrivers } = kpiConfig;

  if (isNil(decklineDrivers)) {
    return null;
  }

  const { valueAccessor, hierarchyAccessor } = decklineDrivers;

  if (isNil(valueAccessor) || isNil(hierarchyAccessor)) {
    return null;
  }

  const hierarchyValue = kpiDrivers[hierarchyAccessor];
  const dimensionValue = kpiDrivers[valueAccessor];

  if (!hierarchyValue || !dimensionValue) {
    return null;
  }

  return [hierarchyValue, dimensionValue].join('-');
};

/**
 * Converts a DimSum node selection into KPI driver key-value pairs.
 * Splits the node ID on the first `-` to extract the hierarchy and dimension values.
 * Returns nullified fields when the node is null (clearing the selection).
 *
 * Returns a {@link KpiDriverValues} bag (no `__typename`): it holds either dataset's fields, so an
 * HCTR dimension selection yields HCTR accessors intact. Today the result flows into a Galaxy-only
 * `onChange`; when the HCTR write path lands, widening `onChange` to `KpiDriverValues` carries these
 * values through unchanged.
 *
 * @param node - The selected DimSum node, or null to clear the selection
 * @param kpiConfig - The KPI dimension configuration containing accessor mappings
 * @returns driver values with the hierarchy and value fields set for this dimension
 */
export const convertDimSumNodeToKpiDriverValues = (
  node: DimSumEnhancedNode | null,
  kpiConfig: KpiDimensionConfig<KpiDimension>,
): KpiDriverValues => {
  if (kpiConfig.baseKpiField.type !== BaseKpiDimensionFieldTypes.DimensionPicker) {
    return {};
  }

  const { decklineDrivers } = kpiConfig;

  if (isNil(decklineDrivers)) {
    return {};
  }

  const { valueAccessor, hierarchyAccessor } = decklineDrivers;

  if (isNil(valueAccessor) || isNil(hierarchyAccessor)) {
    return {};
  }

  if (isNil(node?.nodeID)) {
    // Nullify the related KPIDriver fields when node is null
    return {
      [hierarchyAccessor]: null,
      [valueAccessor]: null,
    };
  }

  // Build the actual values for the related KPIDriver fields
  const { value: dimensionValue, hierarchy: hierarchyValue } = splitNodeId(node!.nodeID);

  return {
    [hierarchyAccessor]: hierarchyValue,
    [valueAccessor]: dimensionValue,
  };
};
